import { useState, useEffect, useRef } from 'react';
import { Search, Bell, Download, FileText, Copy, Check } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AuditRun } from '../../types';
import { Tooltip } from '../shared';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Reports Screen - Generate and export reports
 */
export function ReportBuilder() {
  const { user } = useAuth();
  const [audits, setAudits] = useState<AuditRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadAudits();
  }, [user]);

  const loadAudits = async () => {
    if (!user) return;

    try {
      const auditsQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc')
      );
      const snapshot = await getDocs(auditsQuery);
      setAudits(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AuditRun));
    } catch (error) {
      console.error('Error loading audits:', error);
    } finally {
      setLoading(false);
    }
  };

  const latestAudit = audits.length > 0 ? audits[0] : null;

  const exportToPDF = async () => {
    if (!previewRef.current || !latestAudit) return;

    setExporting(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`AI-Visibility-Report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const generateShareableLink = () => {
    if (!latestAudit) return;

    // Generate a shareable link (in production, this would create a secure public URL)
    const reportId = latestAudit.id;
    const shareableUrl = `${window.location.origin}/reports/shared/${reportId}`;

    navigator.clipboard.writeText(shareableUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Reports</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search reports..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
          <h1 className="text-3xl font-semibold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-1">Generate executive reports and share insights with your team</p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Report Configuration */}
          <div className="col-span-1 space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Report Configuration</h2>

              <div className="space-y-4">
                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t1-primary">
                    <option>Last 7 days</option>
                    <option>Last 30 days</option>
                    <option>Last 90 days</option>
                    <option>All time</option>
                  </select>
                </div>

                {/* Tone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Narrative Tone</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tone" value="professional" defaultChecked className="text-t1-primary" />
                      <span className="text-sm">Professional</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tone" value="executive" className="text-t1-primary" />
                      <span className="text-sm">Executive</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tone" value="technical" className="text-t1-primary" />
                      <span className="text-sm">Technical</span>
                    </label>
                  </div>
                </div>

                {/* Metrics */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Include Metrics</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="text-t1-primary" />
                      <span className="text-sm">Visibility Score</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="text-t1-primary" />
                      <span className="text-sm">Inaccuracies</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="text-t1-primary" />
                      <span className="text-sm">Competitive Benchmark</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="text-t1-primary" />
                      <span className="text-sm">Score Trend</span>
                    </label>
                  </div>
                </div>

                {/* Branding */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Branding</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t1-primary">
                    <option>Kodiak (Growth tier)</option>
                    <option disabled>Custom (Enterprise only)</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  onClick={exportToPDF}
                  disabled={!latestAudit || exporting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  {exporting ? 'Generating PDF...' : 'Download PDF'}
                </button>
                <button
                  onClick={generateShareableLink}
                  disabled={!latestAudit}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {linkCopied ? 'Link Copied!' : 'Copy Shareable Link'}
                </button>
              </div>
            </div>
          </div>

          {/* Report Preview */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
              <h2 className="text-base font-semibold text-gray-900 mb-6">Preview</h2>

              {latestAudit ? (
                <div ref={previewRef} className="space-y-6 text-sm">
                  {/* Report Header */}
                  <div className="border-b border-gray-200 pb-4">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Visibility Report</h1>
                    <p className="text-gray-600">
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>

                  {/* Executive Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Executive Summary</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Your current AI visibility score is <strong>{latestAudit.visibilityScore}</strong> out of 100.
                      {latestAudit.previousScore && latestAudit.previousScore !== latestAudit.visibilityScore && (
                        <> This represents a {latestAudit.visibilityScore > latestAudit.previousScore ? 'positive' : 'negative'} change
                        of {Math.abs(latestAudit.visibilityScore - latestAudit.previousScore)} points from the previous scan.</>
                      )}
                    </p>
                  </div>

                  {/* Key Metrics */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Metrics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded p-4">
                        <p className="text-xs text-gray-600 uppercase mb-1">Visibility Score</p>
                        <p className="text-3xl font-bold text-t1-primary">{latestAudit.visibilityScore}</p>
                      </div>
                      <div className="bg-gray-50 rounded p-4">
                        <p className="text-xs text-gray-600 uppercase mb-1">Queries Scanned</p>
                        <p className="text-3xl font-bold text-gray-900">{latestAudit.totalQueries}</p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommendations</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Review and address critical inaccuracies in the Fix List</li>
                      <li>Update structured data to improve AI platform visibility</li>
                      <li>Monitor competitor activity to maintain market position</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <Tooltip content="Run an audit to generate reports with real data">
                  <div className="flex flex-col items-center justify-center h-96 text-gray-400">
                    <FileText className="w-16 h-16 mb-4" />
                    <p className="text-sm">No audit data available</p>
                    <p className="text-xs mt-1">Run your first scan to generate reports</p>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
